import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Loading from "@/components/Loading";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import {
  type ComboboxOption,
  MultiSelectCombobox,
} from "../../../InfoHub/Components/Form/Components/MultiSelectCombobox";
import { TraderTypeMatrix } from "./components/TraderTypeMatrix";
import {
  type ExchangeLocationModalOptions,
  type ExchangeLocationType,
  type TraderOrganization,
} from "../../types";

export const EXCHANGE_LOCATION_FORM_ID = "exchange-location-form";

const entityIdSchema = z.union([z.string(), z.number()]);

const comboboxOptionSchema = z.object({
  depth: z.number().optional(),
  label: z.string(),
  value: entityIdSchema,
});

export const createExchangeLocationSchema = (requiredMessage: string) =>
  z.object({
    active: z.boolean().default(false),
    assignments: z
      .array(z.object({ id: entityIdSchema, list: z.array(entityIdSchema) }))
      .default([]),
    location: entityIdSchema
      .nullable()
      .default(null)
      .refine(value => value !== null && value !== "", { error: requiredMessage }),
    name: z.preprocess(value => value ?? "", z.string().trim().min(1, { error: requiredMessage })),
    traders: z.array(comboboxOptionSchema).min(1, { error: requiredMessage }),
    types: z.array(entityIdSchema).min(1, { error: requiredMessage }),
  });

export type ExchangeLocationFormValues = z.infer<ReturnType<typeof createExchangeLocationSchema>>;

interface FormProps {
  areaOptions?: ComboboxOption[];
  exchangeLocationTypes?: ExchangeLocationType[];
  initialValues?: Partial<ExchangeLocationFormValues>;
  isFetching?: boolean;
  modalOptions: ExchangeLocationModalOptions;
  onSubmit: (values: ExchangeLocationFormValues) => void;
  traders?: TraderOrganization[];
}

const normalizeInitialValues = (
  initialValues?: Partial<ExchangeLocationFormValues>
): ExchangeLocationFormValues => ({
  active: initialValues?.active ?? false,
  assignments: initialValues?.assignments ?? [],
  location: initialValues?.location ?? null,
  name: initialValues?.name ?? "",
  traders: initialValues?.traders ?? [],
  types: initialValues?.types ?? [],
});

export const Form = ({
  areaOptions = [],
  exchangeLocationTypes = [],
  initialValues,
  isFetching = false,
  modalOptions,
  onSubmit,
  traders = [],
}: FormProps) => {
  const { t } = useTranslation();
  const isViewMode = modalOptions.type === "view";
  const schema = useMemo(
    () => createExchangeLocationSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );
  const defaultValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<ExchangeLocationFormValues>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<ExchangeLocationFormValues>,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, modalOptions.id, modalOptions.type, reset]);

  const selectedTypeIds = useWatch({ control, name: "types" });
  const selectedTraderOptions = useWatch({ control, name: "traders" });

  const selectedTypes = useMemo(
    () =>
      exchangeLocationTypes.filter(exchangeLocationType =>
        (selectedTypeIds ?? []).some(typeId => String(typeId) === String(exchangeLocationType.id))
      ),
    [exchangeLocationTypes, selectedTypeIds]
  );
  const selectedTraders = useMemo(
    () =>
      traders.filter(trader =>
        (selectedTraderOptions ?? []).some(option => String(option.value) === String(trader.id))
      ),
    [selectedTraderOptions, traders]
  );

  const traderOptions = useMemo(
    () => traders.map(trader => ({ label: trader.name ?? "", value: trader.id ?? "" })),
    [traders]
  );

  const getFieldError = (fieldName: keyof ExchangeLocationFormValues) =>
    errors[fieldName]?.message ?? "";

  if (isFetching) {
    return <Loading isVisible />;
  }

  return (
    <form
      id={EXCHANGE_LOCATION_FORM_ID}
      className="flex flex-col gap-5"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <p className="text-muted-foreground text-sm">{t("viewExchangeDetailsModal.helperText")}</p>

      <Controller
        control={control}
        name="name"
        render={({ field }) => {
          const error = getFieldError("name");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-name">
                {t("viewExchangeDetailsModal.outletLocationName.label")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Input
                {...field}
                id="input-name"
                value={field.value ?? ""}
                disabled={isViewMode}
                placeholder={t("viewExchangeDetailsModal.outletLocationName.placeholder")}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-name-error" : undefined}
              />
              <FieldError id="input-name-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="location"
        render={({ field }) => {
          const error = getFieldError("location");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-location">
                {t("viewExchangeDetailsModal.functioningArea.label")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <MultiSelectCombobox
                id="input-location"
                singleSelect
                options={areaOptions}
                value={areaOptions.filter(
                  option => String(option.value) === String(field.value ?? "")
                )}
                placeholder={t("viewExchangeDetailsModal.functioningArea.placeholder")}
                errorMessage={error}
                isDisabled={isViewMode}
                onChange={options => field.onChange(options[0]?.value ?? null)}
              />
              <FieldError id="input-location-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="types"
        render={({ field }) => {
          const error = getFieldError("types");
          const selected = field.value ?? [];

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-types">
                {t("viewExchangeDetailsModal.type")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <div id="input-types" className="flex flex-wrap items-center gap-4">
                {exchangeLocationTypes.map(exchangeLocationType => {
                  const typeId = exchangeLocationType.id ?? "";
                  const checkboxId = `input-type-${typeId}`;

                  return (
                    <div key={String(typeId)} className="flex items-center gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={selected.some(value => String(value) === String(typeId))}
                        disabled={isViewMode}
                        onCheckedChange={checked =>
                          field.onChange(
                            checked === true
                              ? [...selected, typeId]
                              : selected.filter(value => String(value) !== String(typeId))
                          )
                        }
                      />
                      <Label htmlFor={checkboxId}>{exchangeLocationType.name}</Label>
                    </div>
                  );
                })}
              </div>
              <FieldError id="input-types-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="traders"
        render={({ field }) => {
          const error = getFieldError("traders");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-traders">
                {t("viewExchangeDetailsModal.linkedTraders.label")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <FieldDescription>
                {t("viewExchangeDetailsModal.linkedTraders.helperText")}
              </FieldDescription>
              <MultiSelectCombobox
                id="input-traders"
                options={traderOptions}
                value={field.value ?? []}
                placeholder={t("viewExchangeDetailsModal.linkedTraders.placeholder")}
                errorMessage={error}
                isDisabled={isViewMode}
                onChange={field.onChange}
              />
              <FieldError id="input-traders-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      {selectedTraders.length > 0 && selectedTypes.length > 0 ? (
        <Controller
          control={control}
          name="assignments"
          render={({ field }) => (
            <TraderTypeMatrix
              exchangeLocationTypes={selectedTypes}
              isDisabled={isViewMode}
              traders={selectedTraders}
              value={field.value ?? []}
              onChange={field.onChange}
            />
          )}
        />
      ) : null}

      <Controller
        control={control}
        name="active"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Switch
              id="input-active"
              checked={Boolean(field.value)}
              disabled={isViewMode}
              name={field.name}
              onCheckedChange={checked => field.onChange(checked === true)}
            />
            <Label htmlFor="input-active" className="font-semibold text-foreground">
              {t("viewExchangeDetailsModal.activeLocation")}
            </Label>
          </div>
        )}
      />
    </form>
  );
};
